/**
 * 平台自動核准（批155）的畫面判讀——單一入口。
 *
 * 規則（權威在後端 `POST /api/platform/v1/tenants/{ref}/license-requests`）：平台層
 * （`platforms.auto_approve`）與套裝層（`product_plans.auto_approve`）兩個開關**都打開**，
 * 而且「將簽出的內容」逐字等於套裝模板、期限在允許清單內、續約只預先一期，平台送的申請
 * 才會當場核准（`issued_via='platform'`、`reviewed_by` 留 null）。任一不成立照常轉人審，
 * 申請帶 `review_reason` 說明為什麼。
 *
 * ★ `review_reason`（這裡）與 `review.reason`（我方退回／婉拒時寫給送件方看的理由）是兩件事。
 */
import type { LicenseReq } from '@/api/ls'

/** 自動核准的申請（`issued_via='platform'`）。 */
export const AUTO_APPROVE_ISSUED_VIA = 'platform'

/** 沒有自動核准的原因 → 中文（後端值域見 `docs/PLATFORM_API.md` §4.6）。 */
export const REVIEW_REASON_LABELS: Record<string, string> = {
  auto_approve_off: '自動核准沒有開（平台或套裝其中一層的開關是關的）',
  internal_plan: '涉及內部套裝（申請的套裝或現行證的套裝是內部套裝），一律人工審核',
  content_differs: '送件內容與套裝模板不一致',
  term_not_allowed: '期限不在套裝或產品允許的清單內',
  renew_too_early: '續約太早：現行證剩下的期間超過一期',
}

/** 原因代碼 → 中文；查不到回原字串（後端新增原因而前端還沒跟上時，寧可印代碼也不要空白）。 */
export function reviewReasonLabel(code: string | null | undefined): string {
  if (!code) return '—'
  return REVIEW_REASON_LABELS[code] ?? code
}

/** 這筆申請是平台自動核准的。 */
export function isAutoApproved(r: Pick<LicenseReq, 'issued_via'> | null | undefined): boolean {
  return r?.issued_via === AUTO_APPROVE_ISSUED_VIA
}

/**
 * 平台申請沒有自動核准的原因代碼。投影欄位優先，舊列或後端放在 `request_json` 的情況退回讀那裡
 * （兩個位置都沒有＝null）。只有平台來源的申請才有意義。
 */
export function autoReviewReason(r: Pick<LicenseReq, 'review_reason' | 'request_json'> | null | undefined): string | null {
  if (!r) return null
  if (typeof r.review_reason === 'string' && r.review_reason) return r.review_reason
  const raw = r.request_json?.review_reason
  return typeof raw === 'string' && raw ? raw : null
}
